import * as d3 from "d3";
import {HorizontalAlign, LabelAlignment, VerticalAlign} from "../../../apis/LabelAlignment";
import {LabelPosition} from "../../../apis/LabelPosition";
import {TEXTAREA_CLASS_NAME} from "../node/AbstractNodeRenderer";

export interface TextElementConfig {
    id: string;
    fontFamily: string;
    fontSize: number | string;
    fontStyle: string;
    fontWeight: string;
    textDecoration: string;
    fillColor: string;
    className?: string;
}

export interface LabelConfig {
    textConfig: TextElementConfig;
    labelPosition?: LabelPosition | null;
    labelAlignment?: LabelAlignment | null;
    maxWidth?: number;
    maxLines?: number;
}

export interface LabelRenderResult {
    textElement: d3.Selection<SVGForeignObjectElement, any, any, any>;
}

export default class LabelRenderingUtils {

    private static readonly DEFAULT_MAX_WIDTH = 100;
    private static readonly DEFAULT_MAX_LINES = 3;
    private static readonly LINE_HEIGHT = 1.2;

    static getLabelRenderPoint(linePath: d3.Selection<SVGPathElement, any, any, any>,
            labelPosition?: { x?: number, y?: number } | null) {
        const pathNode = linePath.node() as SVGPathElement;
        const centerPoint = pathNode.getPointAtLength(pathNode.getTotalLength() / 2);
        return {
            x: labelPosition?.x ?? centerPoint.x,
            y: labelPosition?.y ?? centerPoint.y,
        };
    }

    static createStyledTextElement(text: string,
            svgGroup: d3.Selection<SVGGElement, any, any, any>,
            config: TextElementConfig,
            area: { x: number, y: number, w: number, h: number },
            horizontalAlign?: HorizontalAlign,
            verticalAlign?: VerticalAlign) {

        const foreignObject = svgGroup
                .append("foreignObject")
                .classed(TEXTAREA_CLASS_NAME, true)
                .attr("id", config.id)
                .attr("x", area.x)
                .attr("y", area.y)
                .attr("width", area.w)
                .attr("height", area.h)
                .attr("overflow", "visible");
        if (config.className) {
            foreignObject.classed(config.className, true);
        }

        const div = foreignObject
                .append("xhtml:div")
                .style("display", "flex")
                .style("flex-direction", "column")
                .style("width", `${area.w}px`)
                .style("height", `${area.h}px`)
                .style("overflow", "hidden");

        div.append("xhtml:p")
                .attr("style", this.generateCssStyle(config, horizontalAlign, verticalAlign))
                .text(text);

        return foreignObject;
    }

    static generateCssStyle(config: TextElementConfig, horizontalAlign?: HorizontalAlign, verticalAlign?: VerticalAlign) {
        let css = "";
        css += "color: " + config.fillColor + ";";
        css += "font-family: " + config.fontFamily + ";";
        css += "font-size: " + config.fontSize + "px;";
        css += "font-style: " + config.fontStyle + ";";
        css += "font-weight: " + config.fontWeight + ";";
        css += "text-decoration: " + config.textDecoration + ";";

        let textAlign = "center";
        if (horizontalAlign === HorizontalAlign.LEFT) textAlign = "left";
        else if (horizontalAlign === HorizontalAlign.RIGHT) textAlign = "right";
        css += "text-align: " + textAlign + ";";

        let alignItems = "center";
        if (verticalAlign === VerticalAlign.TOP) alignItems = "flex-start";
        else if (verticalAlign === VerticalAlign.BOTTOM) alignItems = "flex-end";

        css += "flex: 1; display: flex; align-items: " + alignItems + "; justify-content: " + textAlign + ";";
        css += "margin: 0; padding: 0; border: 0; white-space: pre-line; user-select: none;";
        css += "line-height: " + LabelRenderingUtils.LINE_HEIGHT + "em;";
        css += "overflow-wrap: break-word;";
        css += "paint-order: stroke fill;";
        css += "-webkit-text-stroke: " + LabelRenderingUtils.TEXT_STROKE_WIDTH + "px white;";
        css += "text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white;";

        return css;
    }

    private static readonly TEXT_STROKE_WIDTH = 3;
    private static measureCanvas: HTMLCanvasElement | null = null;

    static measureTextWidth(text: string, textConfig: TextElementConfig) {
        if (text.length === 0) {
            return 0;
        }
        if (!LabelRenderingUtils.measureCanvas) {
            LabelRenderingUtils.measureCanvas = document.createElement("canvas");
        }
        const ctx = LabelRenderingUtils.measureCanvas.getContext("2d")!;
        ctx.font = `${textConfig.fontStyle} ${textConfig.fontWeight} ${textConfig.fontSize}px ${textConfig.fontFamily}`;
        return ctx.measureText(text).width + 2 * LabelRenderingUtils.TEXT_STROKE_WIDTH;
    }

    static applySelectionStyle(visible: boolean, foreignObjectSelector: string) {
        const div = d3.select(foreignObjectSelector).select("div");
        div.style("outline", visible ? "1px solid rgba(0, 0, 0, 0.25)" : "none");
        div.style("outline-offset", visible ? "2px" : "0");
    }

    static renderLabel(
            svgGroup: d3.Selection<SVGGElement, any, any, any>,
            linePath: d3.Selection<SVGPathElement, any, any, any>,
            label: string,
            config: LabelConfig): LabelRenderResult {

        const renderPoint = LabelRenderingUtils.getLabelRenderPoint(linePath, config.labelPosition);
        const area = LabelRenderingUtils.computeLabelArea(renderPoint, label, config);

        const textElement = LabelRenderingUtils.createStyledTextElement(
                label, svgGroup, config.textConfig, area,
                config.labelAlignment?.horizontal,
                config.labelAlignment?.vertical);

        return {textElement};
    }

    private static computeLabelArea(
            renderPoint: { x: number, y: number },
            label: string,
            config: LabelConfig): { x: number, y: number, w: number, h: number } {

        if (config.labelPosition?.w != null && config.labelPosition?.h != null) {
            return {
                x: renderPoint.x,
                y: renderPoint.y,
                w: config.labelPosition.w,
                h: config.labelPosition.h,
            };
        }

        const fontSize = typeof config.textConfig.fontSize === "string"
                ? parseFloat(config.textConfig.fontSize)
                : config.textConfig.fontSize;

        const maxWidth = config.maxWidth ?? LabelRenderingUtils.DEFAULT_MAX_WIDTH;
        const maxLines = config.maxLines ?? LabelRenderingUtils.DEFAULT_MAX_LINES;
        const lineHeight = fontSize * LabelRenderingUtils.LINE_HEIGHT;
        const {lines, maxLineWidth} = LabelRenderingUtils.wrapWords(label, maxWidth, maxLines, config.textConfig);
        const width = Math.max(maxLineWidth, 1);
        const height = lineHeight * lines;

        return {
            x: renderPoint.x - width / 2,
            y: renderPoint.y - height,
            w: width,
            h: height,
        };
    }

    private static wrapWords(text: string, maxWidth: number, maxLines: number, textConfig: TextElementConfig)
            : { lines: number, maxLineWidth: number } {
        const words = text.split(/\s+/).filter(w => w.length > 0);
        if (words.length === 0) {
            return {lines: 1, maxLineWidth: 0};
        }

        const spaceWidth = LabelRenderingUtils.measureTextWidth(" ", textConfig);
        let lines = 1;
        let currentLineWidth = 0;
        let maxLineWidth = 0;

        for (const word of words) {
            const wordWidth = LabelRenderingUtils.measureTextWidth(word, textConfig);
            const acumulatedWidth = currentLineWidth === 0 ? wordWidth : currentLineWidth + spaceWidth + wordWidth;

            if (currentLineWidth > 0 && acumulatedWidth > maxWidth) {
                maxLineWidth = Math.max(maxLineWidth, currentLineWidth);
                if (lines >= maxLines) {
                    break;
                }
                lines++;
                currentLineWidth = wordWidth;
            } else {
                currentLineWidth = acumulatedWidth;
            }
        }
        maxLineWidth = Math.max(maxLineWidth, currentLineWidth) + (2 * spaceWidth);

        return {lines, maxLineWidth};
    }
}
